import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { db, storage } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AdminContext = createContext();

const ADMIN_CREDENTIALS = {
  email: 'admin@indisisters.com',
  password: 'admin123'
};

export const AdminProvider = ({ children }) => {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => {
    return localStorage.getItem('adminLoggedIn') === 'true';
  });

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Listen to Firestore products collection in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'products'),
      (snapshot) => {
        const productsList = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          firestoreId: docSnap.id,
          id: docSnap.data().id || docSnap.id
        }));
        setProducts(productsList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Listen to Firestore categories collection in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const categoriesList = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          firestoreId: docSnap.id
        }));
        setCategories(categoriesList);
      },
      (error) => {
        console.error('Error fetching categories:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Listen to Firestore orders collection in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'orders'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const ordersList = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          firestoreId: docSnap.id
        }));
        setOrders(ordersList);
      },
      (error) => {
        console.error('Error fetching orders:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    localStorage.setItem('adminLoggedIn', isAdminLoggedIn);
  }, [isAdminLoggedIn]);

  const allProducts = useMemo(() => products, [products]);

  const adminLogin = (email, password) => {
    if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
      setIsAdminLoggedIn(true);
      return { success: true };
    }
    return { success: false, error: 'Invalid admin credentials' };
  };

  const adminLogout = () => {
    setIsAdminLoggedIn(false);
  };

  // Upload image file to Firebase Storage and return download URL
  const uploadImage = useCallback(async (file) => {
    const filename = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `products/${filename}`);
    await uploadBytes(storageRef, file, { contentType: file.type });
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  }, []);

  // Upload multiple image files
  const uploadImages = useCallback(async (files) => {
    const urls = [];
    for (const file of files) {
      const url = await uploadImage(file);
      urls.push(url);
    }
    return urls;
  }, [uploadImage]);

  const addProduct = useCallback(async (product, imageFiles = []) => {
    const id = Date.now();
    const slug = product.name.toLowerCase().replace(/\s+/g, '-');

    // Upload image files if provided
    let imageUrls = product.images || [];
    if (imageFiles.length > 0) {
      imageUrls = await uploadImages(imageFiles);
    }

    const newProduct = {
      ...product,
      id,
      slug,
      images: imageUrls,
      createdAt: new Date().toISOString()
    };

    await setDoc(doc(db, 'products', String(id)), newProduct);
    return newProduct;
  }, [uploadImages]);

  const updateProduct = useCallback(async (id, updatedFields, imageFiles = []) => {
    if (imageFiles.length > 0) {
      const newImageUrls = await uploadImages(imageFiles);
      updatedFields.images = [...(updatedFields.images || []), ...newImageUrls];
    }

    if (updatedFields.name) {
      updatedFields.slug = updatedFields.name.toLowerCase().replace(/\s+/g, '-');
    }

    // Find the correct Firestore document ID
    const product = products.find(p => p.id === id || p.id === String(id) || p.firestoreId === String(id));
    const docId = product?.firestoreId || String(id);

    console.log('updateProduct called - id:', id, 'docId:', docId, 'fields:', Object.keys(updatedFields));
    await updateDoc(doc(db, 'products', docId), updatedFields);
  }, [uploadImages, products]);

  const toggleBestSeller = useCallback(async (id) => {
    const product = products.find(p => p.id === id || p.id === String(id));
    if (!product) return;
    const docId = product.firestoreId || String(id);
    await updateDoc(doc(db, 'products', docId), {
      isBestSeller: !product.isBestSeller
    });
  }, [products]);

  const deleteProduct = useCallback(async (id) => {
    const product = products.find(p => p.id === id || p.firestoreId === String(id));
    const docId = product?.firestoreId || String(id);
    await deleteDoc(doc(db, 'products', docId));
  }, [products]);

  const updateOrderStatus = useCallback(async (firestoreId, status) => {
    await updateDoc(doc(db, 'orders', firestoreId), { status });
  }, []);

  // Main Categories management (Women's, Men's, Kids)
  const [mainCategories, setMainCategories] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'mainCategories'),
      (snapshot) => {
        const list = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          firestoreId: docSnap.id
        }));
        setMainCategories(list);
      },
      (error) => {
        console.error('Error fetching main categories:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Sub Categories management (Kurti, Co-ord Sets, etc.)
  const [subCategories, setSubCategories] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'subCategories'),
      (snapshot) => {
        const list = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          firestoreId: docSnap.id
        }));
        setSubCategories(list);
      },
      (error) => {
        console.error('Error fetching sub categories:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  const addMainCategory = useCallback(async (category, imageFile) => {
    let imageUrl = category.image || '';
    if (imageFile) {
      imageUrl = await uploadImage(imageFile);
    }
    const id = Date.now().toString();
    const data = {
      ...category,
      image: imageUrl,
      slug: category.name.toLowerCase().replace(/['']/g, '').replace(/\s+/g, '-'),
      createdAt: new Date().toISOString()
    };
    await setDoc(doc(db, 'mainCategories', id), data);
  }, [uploadImage]);

  const updateMainCategory = useCallback(async (firestoreId, updatedFields, imageFile) => {
    if (imageFile) {
      updatedFields.image = await uploadImage(imageFile);
    }
    if (updatedFields.name) {
      updatedFields.slug = updatedFields.name.toLowerCase().replace(/['']/g, '').replace(/\s+/g, '-');
    }
    await updateDoc(doc(db, 'mainCategories', firestoreId), updatedFields);
  }, [uploadImage]);

  const deleteMainCategory = useCallback(async (firestoreId) => {
    await deleteDoc(doc(db, 'mainCategories', firestoreId));
  }, []);

  const addSubCategory = useCallback(async (category, imageFile) => {
    let imageUrl = category.image || '';
    if (imageFile) {
      imageUrl = await uploadImage(imageFile);
    }
    const id = Date.now().toString();
    const data = {
      ...category,
      image: imageUrl,
      slug: category.name.toLowerCase().replace(/\s+/g, '-'),
      createdAt: new Date().toISOString()
    };
    await setDoc(doc(db, 'subCategories', id), data);
  }, [uploadImage]);

  const updateSubCategory = useCallback(async (firestoreId, updatedFields, imageFile) => {
    if (imageFile) {
      updatedFields.image = await uploadImage(imageFile);
    }
    if (updatedFields.name) {
      updatedFields.slug = updatedFields.name.toLowerCase().replace(/\s+/g, '-');
    }
    await updateDoc(doc(db, 'subCategories', firestoreId), updatedFields);
  }, [uploadImage]);

  const deleteSubCategory = useCallback(async (firestoreId) => {
    await deleteDoc(doc(db, 'subCategories', firestoreId));
  }, []);

  return (
    <AdminContext.Provider value={{
      isAdminLoggedIn,
      adminLogin,
      adminLogout,
      allProducts,
      categories,
      orders,
      addProduct,
      updateProduct,
      toggleBestSeller,
      deleteProduct,
      uploadImage,
      uploadImages,
      updateOrderStatus,
      mainCategories,
      subCategories,
      addMainCategory,
      updateMainCategory,
      deleteMainCategory,
      addSubCategory,
      updateSubCategory,
      deleteSubCategory,
      loading
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error('useAdmin must be used within AdminProvider');
  return context;
};
